'use client';

import { useRef, useState, useEffect, type MouseEventHandler } from 'react';
import cn from 'classnames';
import Shelf from 'components/containers/shelf';
import FeaturedImageViewer from 'components/collection/modal-viewer/modal-collection-viewer';
import Modal3DViewer from 'components/collection/modal-viewer/modal-3d-viewer';
import RightsRestrictedMessage from 'components/collection/rights-restricted-message';
import NoImageImage from 'assets/icons/no-image.svg';
import ImageButtons from './image-buttons';
import styles from './image-viewer.module.scss';
import { type IVntanaAsset } from '@metmuseum/types';
import { useTranslations } from 'next-intl';

const ImageViewer = ({
	images,
	title,
	printUrl,
	isRightToLeft = false,
	vntanaAssets = [],
	objectID = null,
	onAnalyticsEvent,
	activeIndex,
	onIndexChange,
}: IProps) => {
	const t = useTranslations('collection');

	const isControlled = activeIndex !== undefined;
	const [internalIndex, setInternalIndex] = useState(activeIndex ?? 0);
	const currentImageIndex = isControlled ? activeIndex : internalIndex;
	const [featuredImageOpen, setFeaturedImageOpen] = useState(false);
	const [show3D, setShow3D] = useState(false);
	const currentSlideRef = useRef<HTMLButtonElement>(undefined);
	const featuredImageRef = useRef<HTMLButtonElement>(undefined); // Ref for controlling the <Slideshow> component

	const primaryImage = images[0];
	const additionalImages = images.slice(1);
	const featuredImage = images[currentImageIndex];
	const hasFeaturedImage = !!featuredImage.webImageUrl;
	const hasRightsRestrictedImage =
		featuredImage.webImageUrl && !featuredImage.isOpenAccess; //Both has an image and one that is not open access.

	const setCurrentImageIndex = (next: number) => {
		if (!isControlled) {
			setInternalIndex(next);
		}
		onIndexChange?.(next);
	};

	//clicking the featured image opens the slideshow modal
	const handleFeaturedImageClick = () => {
		if (featuredImage.isOpenAccess) {
			// first check if the image is open access
			featuredImageRef?.current?.scrollIntoView({ behavior: 'smooth' }); // move the hidden slideshow to the index of the current image
			setFeaturedImageOpen(true); // show the slideshow
		}
	};

	const toggle3DImage = () => {
		setShow3D(!show3D);
	};

	const handleCloseModal = () => {
		if (currentSlideRef?.current) {
			//If there is only one image, there are no thumbnails to scroll into view
			currentSlideRef.current.scrollIntoView({
				inline: 'center',
				block: 'end',
			});
		}

		setFeaturedImageOpen(false);
	};

	const handleThumbnailImageClick = (index) => {
		if (index === currentImageIndex) {
			handleFeaturedImageClick();
		}
		setCurrentImageIndex(index);
	};

	useEffect(() => {
		if (featuredImageOpen) {
			featuredImageRef?.current?.focus(); // pass focus to the <Slideshow> when it's visible
		}
	}, [featuredImageOpen]);

	return (
		<div className={styles.wrapper}>
			<section className={styles.imageAndControls}>
				<div className={styles.imageAndRightsContainer}>
					<figure
						itemType="http://schema.org/ImageObject"
						className={styles.figure}
					>
						{hasFeaturedImage ? (
							<FeaturedImage
								image={featuredImage}
								handleFeaturedImageClick={handleFeaturedImageClick}
							/>
						) : (
							<>
								<NoImageImage className={styles.noFeaturedImageImage} />
								<div className={styles.noFeaturedImageMessage}>
									No image available
								</div>
							</>
						)}
					</figure>

					<div className={styles.featuredImageMeta}>
						<div className={styles.featuredImageAccess} />
						{/* caption and/or image rights message */}
						{(featuredImage.publicCaption || hasRightsRestrictedImage) && (
							<div className={styles.featuredImageContext}>
								{hasRightsRestrictedImage && (
									<div className={styles.rightsRestrictedMessageWrapper}>
										<RightsRestrictedMessage />
									</div>
								)}
								{featuredImage.publicCaption && (
									<div className={styles.captionWrapper}>
										{featuredImage.publicCaption}
									</div>
								)}
							</div>
						)}
					</div>
					<ImageButtons
						printUrl={printUrl}
						isOpenAccess={featuredImage.isOpenAccess}
						title={title}
						objectID={objectID}
						vntanaAssets={vntanaAssets}
						downloadUrl={featuredImage.originalImageUrl}
						thumbnailUrl={primaryImage.webImageUrl}
						toggle3DImage={toggle3DImage}
						onFeaturedImageClick={handleFeaturedImageClick}
						onAnalyticsEvent={onAnalyticsEvent}
					/>
				</div>
				{/* additional images thumbnail shelf */}
				{additionalImages?.length > 0 && (
					<div
						className={cn(styles.shelfContainer, {
							[styles.rtl]: isRightToLeft,
						})}
					>
						<Shelf
							isRightToLeft={isRightToLeft}
							gap="10px"
							hasClampedWidth={true}
						>
							{images.map((image, index) => (
								<button
									ref={currentImageIndex === index ? currentSlideRef : null}
									key={image.webImageUrl}
									onClick={() => handleThumbnailImageClick(index)}
									className={styles.thumbnailContainer}
								>
									{/* eslint-disable @next/next/no-img-element */}
									<img
										alt={image.altText}
										className={`${styles.thumbnail} ${currentImageIndex === index ? styles.selectedThumbnail : ''}`}
										loading="lazy"
										src={image.webImageUrl}
									/>
									{/* eslint-enable @next/next/no-img-element */}
								</button>
							))}
						</Shelf>
						{isRightToLeft && <div className={styles.rtlText}>{t('rtl')}</div>}
					</div>
				)}
			</section>
			{featuredImageOpen && (
				<FeaturedImageViewer
					ref={featuredImageRef}
					isRightToLeft={isRightToLeft}
					onClose={handleCloseModal}
					activeIndex={currentImageIndex}
					onIndexChange={setCurrentImageIndex}
					images={images}
					objectID={objectID}
					objectTitle={title}
				/>
			)}

			{show3D && (
				<Modal3DViewer
					onClose={toggle3DImage}
					vntanaAssets={vntanaAssets}
					alt={featuredImage.altText}
					objectID={objectID}
					objectTitle={title}
					isOpenAccess={primaryImage.isOpenAccess}
				/>
			)}
		</div>
	);
};

export interface IProps {
	title: string;
	images: ImageViewerImage[];
	vntanaAssets?: IVntanaAsset[] | null;
	isRightToLeft?: boolean;
	printUrl?: string;
	objectID?: number | string;
	onAnalyticsEvent?: (event: ImageViewerAnalyticsEvent) => void;
	activeIndex?: number;
	onIndexChange?: (index: number) => void;
}

export interface ImageViewerImage {
	altText: string;
	isOpenAccess: boolean;
	publicCaption?: string;
	webImageUrl: string;
	originalImageUrl?: string;
}

export type ImageViewerAnalyticsEvent =
	| { type: 'view_in_3d' }
	| { type: 'download_3d_model' }
	| { type: 'download_image' }
	| { type: 'buy_a_print' };

const FeaturedImage = ({
	image,
	handleFeaturedImageClick,
}: FeaturedImageProps) => {
	return image.isOpenAccess ? (
		/* eslint-disable @next/next/no-img-element */
		<img
			fetchPriority="high"
			loading="eager"
			role="button"
			alt={image.altText}
			className={`${styles.featuredImage} ${styles.isButton}`}
			src={image.webImageUrl}
			itemProp="contentUrl"
			onClick={handleFeaturedImageClick}
		/>
	) : (
		<img
			fetchPriority="high"
			loading="eager"
			alt={image.altText}
			className={`${styles.featuredImage} ${styles.isRestricted}`}
			src={image.webImageUrl}
			itemProp="contentUrl"
			onClick={handleFeaturedImageClick}
		/>
	);
	/* eslint-enable @next/next/no-img-element */
};

interface FeaturedImageProps {
	image: ImageViewerImage;
	handleFeaturedImageClick: MouseEventHandler<HTMLImageElement>;
}

export default ImageViewer;
